import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getCase, guess, spendHint } from './puzzleApi';
import CaseNotice from './CaseNotice';
import { caseNumber, plainTitle, renderEmphasis, renderSuspectWriteup, renderTitle, difficultyLabel } from './puzzleText';
import './CaseFile.css';

const pageDescription = "Solve it yourself: a Sam Norris mini-mystery from author Jerry Evanoff. Name the killer AND the clue that gives them away.";
const fontsUrl = 'https://fonts.googleapis.com/css2?family=Special+Elite&family=Libre+Franklin:ital,wght@0,400;0,600;0,800;1,400&display=swap';

const CaseFile = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guidParam = searchParams.get('c');

  const [state, setState] = useState({ status: 'loading' });
  const [pickedSuspect, setPickedSuspect] = useState(null);
  const [pickedClue, setPickedClue] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [hinting, setHinting] = useState(false);
  const [wrongFlash, setWrongFlash] = useState(false);
  const [actionFailed, setActionFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setState({ status: 'loading' });
    setPickedSuspect(null);
    setPickedClue(null);
    setWrongFlash(false);
    setActionFailed(false);

    getCase(guidParam)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'ready', data });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: 'error', code: error.code || 'server_error' });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [guidParam]);

  if (state.status === 'loading') {
    return <CaseNotice code="loading" />;
  }

  if (state.status === 'error') {
    return <CaseNotice code={state.code} />;
  }

  const { case: puzzle, progress } = state.data;
  const answerPath = `/answer?c=${encodeURIComponent(puzzle.guid)}`;
  const pageUrl = guidParam
    ? `https://jerryevanoff.com/puzzle?c=${encodeURIComponent(puzzle.guid)}`
    : 'https://jerryevanoff.com/case-file';
  const pageTitle = `${plainTitle(puzzle.title)} | A Sam Norris Mini-Mystery | Jerry Evanoff`;

  const crossedSuspects = progress.crossedOut.suspects;
  const crossedClues = progress.crossedOut.clues;

  const handleHint = async () => {
    if (hinting || progress.hintsRemaining === 0) {
      return;
    }

    setHinting(true);
    setActionFailed(false);

    try {
      const result = await spendHint(puzzle.guid);
      setState((prev) => ({ ...prev, data: { ...prev.data, progress: result.progress } }));
    } catch (error) {
      setActionFailed(true);
    } finally {
      setHinting(false);
    }
  };

  const handleGuess = async () => {
    if (!pickedSuspect || !pickedClue || submitting) {
      return;
    }

    setSubmitting(true);
    setActionFailed(false);
    setWrongFlash(false);

    try {
      const result = await guess(puzzle.guid, pickedSuspect, pickedClue);

      if (result.progress.locked) {
        navigate(answerPath);
        return;
      }

      setState((prev) => ({ ...prev, data: { ...prev.data, progress: result.progress } }));
      setPickedSuspect(null);
      setPickedClue(null);
      setWrongFlash(true);
    } catch (error) {
      setActionFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="case-file-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="author" content="Jerry Evanoff" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Jerry Evanoff" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />

        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link rel="stylesheet" href={fontsUrl} />
      </Helmet>

      <main className="cf-folder">
        <span className="cf-tab">CASE FILE · No. {caseNumber(puzzle.number)}</span>
        <div className="cf-stamp">UNSOLVED</div>

        <h1>{renderTitle(puzzle.title)}</h1>
        <p className="cf-series-note">
          A Sam Norris mini-mystery — from the desk of Jerry Evanoff
          <span className="cf-difficulty">{difficultyLabel(puzzle.difficulty)}</span>
        </p>

        {puzzle.setup.map((paragraph, index) => (
          <p className="cf-brief" key={index}>{paragraph}</p>
        ))}

        {puzzle.image && (
          <figure className="cf-scene">
            <img src={puzzle.image.src} alt={puzzle.image.alt} width="1200" height="900" />
            {puzzle.image.caption && <figcaption className="cf-scene-cap">{puzzle.image.caption}</figcaption>}
          </figure>
        )}

        <h2>The Evidence</h2>
        {puzzle.evidence.map((item) => (
          <div className={`cf-evidence ${crossedClues.includes(item.key) ? 'crossed' : ''}`} key={item.key}>
            <strong>{item.label}:</strong> {item.text}
          </div>
        ))}

        <h2>The Suspects</h2>
        <div className="cf-evidence">
          {puzzle.suspects.map((suspect, index) => (
            <React.Fragment key={suspect.key}>
              {index > 0 && (
                <>
                  <br />
                  <br />
                </>
              )}
              <span className={crossedSuspects.includes(suspect.key) ? 'crossed' : ''}>
                {renderSuspectWriteup(suspect)}
              </span>
            </React.Fragment>
          ))}
        </div>

        {!progress.locked && (
          <>
            <h2>Who's the killer?</h2>
            <ul className="cf-suspects">
              {puzzle.suspects.map((suspect) => {
                const crossed = crossedSuspects.includes(suspect.key);

                return (
                  <li key={suspect.key}>
                    <button
                      type="button"
                      className="cf-suspect"
                      aria-pressed={pickedSuspect === suspect.key}
                      disabled={crossed}
                      onClick={() => setPickedSuspect(suspect.key)}
                    >
                      <span className="cf-name">{suspect.name}{crossed && ' — ruled out'}</span>
                      {!crossed && <span className="cf-desc">{suspect.label}</span>}
                    </button>
                  </li>
                );
              })}
            </ul>

            <h2>What gives them away?</h2>
            <ul className="cf-suspects">
              {puzzle.evidence.map((item) => {
                const crossed = crossedClues.includes(item.key);

                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      className="cf-suspect"
                      aria-pressed={pickedClue === item.key}
                      disabled={crossed}
                      onClick={() => setPickedClue(item.key)}
                    >
                      <span className="cf-name">{item.label}{crossed && ' — ruled out'}</span>
                    </button>
                  </li>
                );
              })}
            </ul>

            {wrongFlash && (
              <p className="cf-wrong-flash" role="status">
                Not quite, detective. {progress.hintsRemaining > 0 ? 'Sam crosses another possibility off the board.' : 'Last chance — choose carefully.'}
              </p>
            )}

            {progress.hint2Text && (
              <div className="cf-hint-box">{renderEmphasis(progress.hint2Text)}</div>
            )}

            {puzzle.hasHint && progress.hintsRemaining > 0 && (
              <button type="button" className="cf-hint-btn" disabled={hinting} onClick={handleHint}>
                {hinting ? 'Thinking…' : `Need a hint from Sam? (${progress.hintsRemaining} hint${progress.hintsRemaining === 1 ? '' : 's'} left)`}
              </button>
            )}

            <div className="cf-actions">
              <button
                type="button"
                className="cf-cta"
                disabled={!pickedSuspect || !pickedClue || submitting}
                onClick={handleGuess}
              >
                {submitting ? 'Making the accusation…' : 'Make the accusation'}
              </button>
              {actionFailed && (
                <p className="cf-error" role="alert">
                  That didn't go through. Please try again.
                </p>
              )}
            </div>
          </>
        )}

        {progress.locked && (
          <div className="cf-answered">
            You've already made your accusation on this case.{' '}
            <Link to={answerPath}>See Sam's solution</Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default CaseFile;
